import React from 'react'
import { ItemRenderProps } from '@components/UniversalAPISelector'
import { useBusinessContext } from '@root/context'
import { motion } from 'framer-motion'
import { InputProps } from '@components'
import styled, { css } from 'styled-components'
import { FaCheck } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import UniversalAPISelector from '@root/components/UniversalAPISelector/UniversalAPISelector'
import { useScreen } from '@root/utils'
import { fetchHeader, fetchHeaders, IHeader } from '@root/query/headers'
import { FaBookBookmark } from 'react-icons/fa6'

interface Props extends InputProps {
  onChange: (headerId: number) => void
}

const PAGE_SIZE = 50

export const HeaderSelector: React.FC<Props> = ({ ...rest }) => {
  const { slug } = useBusinessContext()
  const { t } = useTranslation()
  const { isMobile } = useScreen()
  const dataToShow = (item: IHeader): string => item?.name ?? ''

  const renderRow = (
    item: IHeader,
    { isFocused, isSelected, onKeyDown, handleOnItemClick, style }: ItemRenderProps<IHeader>
  ) => {
    return (
      <ItemWrapper
        className={isFocused && 'keyboard-focused'}
        onKeyDown={onKeyDown}
        onClick={() => handleOnItemClick(item)}
        style={style}
        key={item.id}
      >
        <Item level={item.level}>
          <ItemColumn $isMobile={isMobile}>{item.name}</ItemColumn>
          <RightItemsWrapper>
            {isSelected && (
              <ItemColumn $isMobile={isMobile}>
                <CheckMarkWrapper>
                  <FaCheck />
                </CheckMarkWrapper>
              </ItemColumn>
            )}
          </RightItemsWrapper>
        </Item>
      </ItemWrapper>
    )
  }

  return (
    <UniversalAPISelector
      {...rest}
      getItemQueryKeyFn={itemId => [slug, 'headers', itemId]}
      getItem={headerId => fetchHeader({ slug, headerId })}
      getItemsQueryKeyFn={params => [slug, 'headers', { ...params, page_size: PAGE_SIZE }]}
      getItems={params => fetchHeaders({ slug }, { ...params, page_size: PAGE_SIZE })}
      renderRow={renderRow}
      label={t('uas.headers.label')}
      info={t('uas.headers.info')}
      placeholder={t('uas.headers.placeholder')}
      noDataMessage={t('uas.headers.noData')}
      dropDownHeader={t('uas.headers.select')}
      dropdownHeaderIcon={<FaBookBookmark />}
      getDataToShow={dataToShow}
    />
  )
}

const Item = styled(motion.div).attrs({
  variants: {
    hidden: {
      y: 5,
      opacity: 0
    },
    show: {
      y: 0,
      opacity: 1
    }
  }
})<{ level: number }>`
  display: flex;
  width: 100%;
  padding: ${({ theme }) => theme.spacing.xs}rem ${({ theme }) => theme.spacing.xs}rem;
  padding-left: ${({ theme, level }) => theme.spacing.sm + theme.spacing.md * level}rem;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  cursor: pointer;

  ${({ level }) => {
    switch (level) {
      case 0:
        return css`
          font-weight: bolder;
          text-transform: uppercase;
        `
      case 1:
        return css`
          font-weight: bold;
          color: ${({ theme }) => theme.colors.neutralBlack};
        `
      case 2:
        return css`
          font-weight: bold;
          color: ${({ theme }) => theme.colors.neutralBlack};
        `

      case 3:
        return css`
          font-weight: initial;
          text-decoration: underline;
          color: ${({ theme }) => theme.colors.neutralBlack};
        `

      default:
        return css`
          font-weight: initial;
          font-size: ${({ theme }) => theme.fontSize.md}rem;
          color: ${({ theme }) => theme.colors.metalGray};
        `
    }
  }}
`

const EditButtonWrapper = styled.div`
  display: none;
  align-self: center;
`

const ItemWrapper = styled.div`
  display: flex;
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;

  &:hover ${EditButtonWrapper} {
    display: block;
  }
  &.keyboard-focused,
  &:hover {
    background: ${({ theme }) => theme.colors.neutralGray};
  }
`

const RightItemsWrapper = styled.div`
  margin-left: auto;
  display: flex;
`

const ItemColumn = styled.div<{ $isMobile: boolean }>`
  align-self: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.id {
    min-width: ${({ theme }) => theme.spacing.xxl}rem;
    font-size: ${({ theme }) => theme.fontSize.md}rem;
    color: ${({ theme }) => theme.colors.metalGray};
    font-variant-numeric: tabular-nums;
    font-family: 'Roboto Mono', monospace;

    ${({ theme, $isMobile }) =>
      $isMobile ? `margin-right:  0;` : `margin-right:  ${theme.spacing.sm}rem;`}
  }
`
const CheckMarkWrapper = styled.div`
  width: 1.2rem;
  height: 1.2rem;
  margin: ${({ theme }) => theme.spacing.sm}rem;

  svg {
    fill: ${({ theme }) => theme.colors.success};
  }
`
